import React, {ReactNode} from 'react'
import {Box, Skeleton, Typography} from '@mui/material'
import {useTranslation} from 'react-i18next'
import {Link} from 'react-router-dom'
import DashboardCard from '@containers/dashboard/ui/DashboardCard'
import ProgressSegments from '@containers/dashboard/ui/ProgressSegments'
import {IDashboardInfo, IVoyagesRealtime} from '@containers/dashboard/types'
import {IDataDeviceActivity} from '@containers/device/containers/deviceActivity/types'
import {STATUSES_DEVICE_ACTIVITY_TOTAL} from '@containers/device/containers/deviceActivity/constants'
import {PATH_DEVICE_OPERATIONAL_STATISTIC} from '@constants/navigate'
import {useCountUp} from '@containers/dashboard/helpers/useCountUp'

const GREEN = '#639922'
const AMBER = '#EF9F27'
const RED = '#E24B4A'

const format = (value?: number) => (value ?? 0).toLocaleString('uk-UA')

const Count = ({value}: {value: number}) => <>{format(useCountUp(value))}</>

interface IKpiProps {
	label: string
	value: ReactNode
	of?: ReactNode
	hint?: ReactNode
	segments: {value: number, color: string}[]
	loading?: boolean
}

const Kpi = ({label, value, of, hint, segments, loading}: IKpiProps) => (
	<DashboardCard>
		<Typography variant={'body2'} color={'text.secondary'}>{label}</Typography>
		{loading ? <Skeleton width={120} height={36}/> : <Typography variant={'h5'} fontWeight={600} sx={{my: .25}}>
			{value}
			{of && <Typography component={'span'} variant={'body2'} color={'text.secondary'}> / {of}</Typography>}
		</Typography>}
		<Typography variant={'caption'} color={'text.secondary'}>{hint}</Typography>
		<ProgressSegments segments={segments}/>
	</DashboardCard>
)

interface IProps {
	info?: IDashboardInfo
	activity?: IDataDeviceActivity
	voyages?: IVoyagesRealtime[]
}

const DashboardKpis = ({info, activity, voyages}: IProps) => {
	const {t} = useTranslation()

	const totalDevices = STATUSES_DEVICE_ACTIVITY_TOTAL
		.reduce((sum, item) => sum + (activity?.total?.[item.value] ?? 0), 0)
	const online = activity?.total?.totalOnline ?? 0

	const sum = (key: keyof IVoyagesRealtime) =>
		(voyages ?? []).reduce((total, item) => total + (Number(item[key]) || 0), 0)
	// Accounts without routes have nothing to show here.
	const hasRoutes = (voyages ?? []).some((item) => item.routeInfo?.length > 0 || item.sumAllVoyagePlanned > 0)

	return (
		<Box
			sx={{
				gridColumn: '1 / -1',
				display: 'grid',
				gap: '12px',
				gridTemplateColumns: {md: `repeat(${hasRoutes ? 3 : 2}, minmax(0, 1fr))`},
				// Phones: one row that scrolls sideways instead of three stacked cards.
				gridAutoFlow: {xs: 'column', md: 'row'},
				gridAutoColumns: {xs: '82%', md: 'auto'},
				overflowX: {xs: 'auto', md: 'visible'},
				scrollSnapType: {xs: 'x mandatory', md: 'none'},
				pb: {xs: .5, md: 0},
				'& > *': {scrollSnapAlign: 'start', minWidth: 0},
			}}
		>
			<Kpi
				label={t('dashboardPage.balance')}
				value={<Count value={info?.balanceNow ?? 0}/>}
				hint={info && t('dashboardPage.balanceOf', {value: format(info.balanceLastIncrement)})}
				segments={[
					{value: info?.balanceNow ?? 0, color: GREEN},
					{value: Math.max((info?.balanceLastIncrement ?? 0) - (info?.balanceNow ?? 0), 0), color: 'transparent'},
				]}
				loading={!info}
			/>

			<Kpi
				label={t('dashboardPage.devicesOnline')}
				value={<Box component={'span'} sx={{color: GREEN}}><Count value={online}/></Box>}
				of={format(totalDevices)}
				hint={t('dashboardPage.onlineShare', {percent: totalDevices ? Math.round(online / totalDevices * 100) : 0})}
				segments={[{value: online, color: GREEN}, {value: totalDevices - online, color: RED}]}
				loading={!activity}
			/>

			{hasRoutes && <Link to={PATH_DEVICE_OPERATIONAL_STATISTIC} style={{color: 'inherit', textDecoration: 'none'}}>
				<Kpi
					label={t('dashboardPage.voyagesOnRoute')}
					value={<Count value={sum('sumVoyageInRoute')}/>}
					of={format(sum('sumAllVoyagePlanned'))}
					hint={t('dashboardPage.voyagesProblems', {
						notInRoute: format(sum('sumVoyageNotInRoute')),
						gps: format(sum('sumVoyageGpsFailed')),
					})}
					segments={[
						{value: sum('sumVoyageInRoute'), color: GREEN},
						{value: sum('sumVoyageNotInRoute'), color: AMBER},
						{value: sum('sumVoyageGpsFailed'), color: RED},
					]}
				/>
			</Link>}
		</Box>
	)
}

export default DashboardKpis
